import i18n from '~/i18n';
import { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from '~/services/types/identity';

export enum CandidateProfileStatus {
    INCOMPLETE = 'INCOMPLETE',
    COMPLETE = 'COMPLETE',
}

export enum ProfileRequirementType {
    ONLY_PROFILE_REQUIREMENT = 'PROFILE_REQUIREMENT',
    ONLY_BE_DISCOVERED_REQUIREMENT = 'BE_DISCOVERED',
}

export enum CandidateProfileActionId {
    FIRST_NAME = 'FIRST_NAME',
    LAST_NAME = 'LAST_NAME',
    NATIONALITY = 'NATIONALITY',
    SKILLS = 'SKILLS',
    PREFERENCES = 'PREFERENCES',
    STUDY_PROGRAMS = 'STUDY_PROGRAMS',
    PROFILE_PICTURE = 'PROFILE_PICTURE',
    GENDER = 'GENDER',
    WORK_AUTHORIZATION = 'WORK_AUTHORIZATION',
    SUMMARY = 'SUMMARY',
    LANGUAGES = 'LANGUAGES',
    WORK_EXPERIENCE = 'WORK_EXPERIENCE',
    LOCATION = 'LOCATION',
}

export interface CandidateProfileAction {
    id: CandidateProfileActionId;
    name: string;
    status: CandidateProfileStatus;
    priority?: number;
}

export interface CandidateProfileScore {
    // The progress in percent
    progress: number;
    status: CandidateProfileStatus;
    actions: CandidateProfileAction[];
}

export interface ProfileRequirement {
    requirement?: ProfileRequirementType;
    score: number;
    eval: (
        candidate: Partial<Candidate>,
        user: Partial<User>,
        workAuthorizations: Partial<WorkAuthorization>[]
    ) => boolean;
    action: (
        candidate: Partial<Candidate>,
        user: Partial<User>,
        workAuthorizations: Partial<WorkAuthorization>[]
    ) => Omit<CandidateProfileAction, 'status'>;
}

export class CandidateProfileScorer {
    candidate: Partial<Candidate>;
    user: Partial<User>;
    workAuthorizations: Partial<WorkAuthorization>[];

    constructor(
        candidate: Partial<Candidate>,
        user: Partial<User>,
        workAuthorizations: Partial<WorkAuthorization>[]
    ) {
        this.candidate = candidate;
        this.user = user;
        this.workAuthorizations = workAuthorizations;
    }

    private calculateScore(requirement?: ProfileRequirementType): CandidateProfileScore {
        const score: CandidateProfileScore = {
            progress: 0,
            status: CandidateProfileStatus.INCOMPLETE,
            actions: [],
        };

        let totalScore = 0;
        this.getActions(requirement).map(action => {
            totalScore += action.score;
            const actionResult = this.calculateAction(action);
            if (actionResult.status === CandidateProfileStatus.COMPLETE) {
                score.progress += action.score;
            } else {
                score.actions.push(actionResult);
            }
        });

        score.progress = Math.round((score.progress / totalScore) * 100);
        score.status =
            score.progress === 100
                ? CandidateProfileStatus.COMPLETE
                : CandidateProfileStatus.INCOMPLETE;

        return score;
    }

    private calculateAction(action: ProfileRequirement): CandidateProfileAction {
        const status = action.eval(this.candidate, this.user, this.workAuthorizations)
            ? CandidateProfileStatus.INCOMPLETE
            : CandidateProfileStatus.COMPLETE;
        const actionResult = action.action(this.candidate, this.user, this.workAuthorizations);
        return {
            ...actionResult,
            status,
        };
    }

    getProfileScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.ONLY_PROFILE_REQUIREMENT);
    }

    getBeDiscoveredScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.ONLY_BE_DISCOVERED_REQUIREMENT);
    }

    getActions(requirement?: ProfileRequirementType): ProfileRequirement[] {
        return this.actions.filter(
            action => !requirement || !action.requirement || action.requirement === requirement
        );
    }

    getCandidateProfileActions(requirement?: ProfileRequirementType): CandidateProfileAction[] {
        return this.getActions(requirement).map(action => this.calculateAction(action));
    }

    private actions: ProfileRequirement[] = [
        // Required actions for all score calculations
        {
            score: 5,
            eval: (c, user, wa) => !user.firstName,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.FIRST_NAME,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.FIRST_NAME}`)
                    .toString(),
                priority: 100,
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 5,
            eval: (c, user, wa) => !user.lastName,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.LAST_NAME,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.LAST_NAME}`)
                    .toString(),
            }),
        },
        {
            score: 10,
            eval: (c, user, wa) => !c.nationalities || c.nationalities.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.NATIONALITY,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.NATIONALITY}`)
                    .toString(),
                priority: 90,
            }),
        },
        {
            score: 10,
            eval: (c, user, wa) => !c.skills || c.skills.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.SKILLS,
                name: i18n.t(`profile.score.fields.${CandidateProfileActionId.SKILLS}`).toString(),
                priority: 80,
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) =>
                !c.preferences ||
                (!c.preferences.sectors ||
                    c.preferences.sectors.length === 0 ||
                    (!c.preferences.careerFields || c.preferences.careerFields.length === 0)),
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.PREFERENCES,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.PREFERENCES}`)
                    .toString(),
                priority: 70,
            }),
        },
        {
            score: 10,
            eval: (c, user, wa) => !c.studyPrograms || c.studyPrograms.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.STUDY_PROGRAMS,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.STUDY_PROGRAMS}`)
                    .toString(),
                priority: 60,
            }),
        },

        // Required actions for only profile score calculations
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) => !user.avatar,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.PROFILE_PICTURE,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.PROFILE_PICTURE}`)
                    .toString(),
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 5,
            eval: (c, user, wa) => !c.gender || c.gender === 'U',
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.GENDER,
                name: i18n.t(`profile.score.fields.${CandidateProfileActionId.GENDER}`).toString(),
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 5,
            eval: (c, user, wa) => !wa || wa.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.WORK_AUTHORIZATION,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.WORK_AUTHORIZATION}`)
                    .toString(),
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) => !c.summary,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.SUMMARY,
                name: i18n.t(`profile.score.fields.${CandidateProfileActionId.SUMMARY}`).toString(),
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) => !c.languages || c.languages.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.LANGUAGES,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.LANGUAGES}`)
                    .toString(),
            }),
        },
        {
            requirement: ProfileRequirementType.ONLY_PROFILE_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) => !c.workExperiences || c.workExperiences.length === 0,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.WORK_EXPERIENCE,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.WORK_EXPERIENCE}`)
                    .toString(),
            }),
        },

        // Required actions for only be discovered score calculations
        {
            requirement: ProfileRequirementType.ONLY_BE_DISCOVERED_REQUIREMENT,
            score: 10,
            eval: (c, user, wa) => !c.country,
            action: (c, user, wa) => ({
                id: CandidateProfileActionId.LOCATION,
                name: i18n
                    .t(`profile.score.fields.${CandidateProfileActionId.LOCATION}`)
                    .toString(),
                priority: 85,
            }),
        },
    ];
}
